
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { Influencer } from "@/types/influencer";
import { Clock, Package, Calendar } from "lucide-react";
import { Link } from "react-router-dom";

interface Package {
  name: string;
  description: string;
  duration_seconds: number;
  price: number;
}

interface PricingInformationProps {
  influencer: Influencer & { packages?: Package[] };
}

export const PricingInformation = ({ influencer }: PricingInformationProps) => {
  const { t } = useLanguage();
  
  // Get packages from influencer data
  const packages = (influencer as any).packages as Package[] | undefined;
  
  if (!packages || packages.length === 0) return null;
  
  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (remainingSeconds === 0) return `${minutes}m`;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatPrice = (price: number) => {
    return price.toLocaleString();
  };
  
  return (
    <Card className="border-primary/10 overflow-hidden relative">
      <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full -mr-16 -mt-16 blur-3xl"></div>
      <CardContent className="pt-6 relative z-10">
        <h3 className="text-xl font-semibold mb-6 flex items-center gap-2">
          <Package className="h-5 w-5 text-primary" />
          <span className="bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
            Packages
          </span>
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {packages.map((pkg, index) => (
            <div 
              key={index}
              className="p-5 rounded-xl bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/10 hover:shadow-lg transition-all duration-300 hover:border-primary/20 flex flex-col"
            >
              <div className="flex items-start justify-between mb-3">
                <h4 className="font-semibold text-lg text-foreground">{pkg.name}</h4>
                <div className="flex items-center gap-1 text-sm text-muted-foreground bg-primary/10 px-2 py-1 rounded-full">
                  <Clock className="h-3 w-3" />
                  <span>{formatDuration(pkg.duration_seconds)}</span>
                </div>
              </div>
              
              {pkg.description && (
                <p className="text-muted-foreground text-sm mb-4 line-clamp-2 flex-grow">
                  {pkg.description}
                </p>
              )}
              
              <div className="pt-3 border-t border-primary/10 space-y-3">
                <div className="text-2xl font-bold text-primary">
                  {formatPrice(pkg.price)} <span className="text-sm font-normal text-muted-foreground">SAR</span>
                </div>
                <Button 
                  className="w-full bg-gradient-to-r from-primary to-primary/80 hover:shadow-md transition-all"
                  size="sm"
                  asChild
                >
                  <Link to={`/influencers/${influencer.id}/book?package=${encodeURIComponent(pkg.name)}`}>
                    <Calendar className="mr-2 h-4 w-4" />
                    Book This Package
                  </Link>
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
